import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchStore, fetchWidgetConfig, updateWidgetConfig } from '../services/api';

export default function WidgetCustomize() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const [primaryColor, setPrimaryColor] = useState('#3B82F6');
  const [backgroundColor, setBackgroundColor] = useState('#FFFFFF');
  const [textColor, setTextColor] = useState('#1F2937');
  const [position, setPosition] = useState<'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'>('bottom-right');
  const [greeting, setGreeting] = useState('How can I help you today?');
  const [placeholder, setPlaceholder] = useState('Search for products...');

  const { data: storeData } = useQuery({
    queryKey: ['store', id],
    queryFn: () => fetchStore(id!),
    enabled: !!id,
  });

  const { data: configData, isLoading } = useQuery({
    queryKey: ['widget-config', id],
    queryFn: () => fetchWidgetConfig(id!),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (config: any) => updateWidgetConfig(id!, config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['widget-config', id] });
      alert('Widget configuration saved!');
    },
  });

  useEffect(() => {
    if (configData?.config) {
      const config = configData.config;
      setPrimaryColor(config.primary_color);
      setBackgroundColor(config.background_color);
      setTextColor(config.text_color);
      setPosition(config.position);
      setGreeting(config.greeting);
      setPlaceholder(config.placeholder);
    }
  }, [configData]);

  const handleSave = () => {
    updateMutation.mutate({
      primary_color: primaryColor,
      background_color: backgroundColor,
      text_color: textColor,
      position,
      greeting,
      placeholder,
    });
  };

  if (isLoading) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="text-center py-12">Loading...</div>
      </div>
    );
  }

  const getPositionStyles = () => {
    const base = 'fixed z-50';
    switch (position) {
      case 'bottom-right':
        return `${base} bottom-4 right-4`;
      case 'bottom-left':
        return `${base} bottom-4 left-4`;
      case 'top-right':
        return `${base} top-4 right-4`;
      case 'top-left':
        return `${base} top-4 left-4`;
    }
  };

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="mb-6">
        <Link to={`/stores/${id}`} className="text-blue-600 hover:text-blue-700">
          ← Back to {storeData?.store.name || 'Store'}
        </Link>
      </div>

      <h2 className="text-2xl font-semibold text-gray-900 mb-6">Customize Widget</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Settings Panel */}
        <div className="space-y-6">
          {/* Colors */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Colors</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Primary Color
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="h-10 w-20 rounded border border-gray-300"
                  />
                  <input
                    type="text"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Background Color
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    className="h-10 w-20 rounded border border-gray-300"
                  />
                  <input
                    type="text"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Text Color
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={textColor}
                    onChange={(e) => setTextColor(e.target.value)}
                    className="h-10 w-20 rounded border border-gray-300"
                  />
                  <input
                    type="text"
                    value={textColor}
                    onChange={(e) => setTextColor(e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Position */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Position</h3>
            <div className="grid grid-cols-2 gap-3">
              {(['bottom-right', 'bottom-left', 'top-right', 'top-left'] as const).map((pos) => (
                <button
                  key={pos}
                  onClick={() => setPosition(pos)}
                  className={`px-4 py-3 rounded-lg border-2 transition ${
                    position === pos
                      ? 'border-blue-600 bg-blue-50 text-blue-900'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {pos.split('-').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                </button>
              ))}
            </div>
          </div>

          {/* Messages */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Messages</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Greeting Message
                </label>
                <input
                  type="text"
                  value={greeting}
                  onChange={(e) => setGreeting(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Input Placeholder
                </label>
                <input
                  type="text"
                  value={placeholder}
                  onChange={(e) => setPlaceholder(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={updateMutation.isPending}
            className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition disabled:opacity-50 font-medium"
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>

        {/* Preview Panel */}
        <div className="lg:sticky lg:top-6 lg:self-start">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview</h3>

            <div className="relative bg-gray-100 rounded-lg aspect-video overflow-hidden">
              {/* Mock website background */}
              <div className="absolute inset-0 p-8">
                <div className="bg-white/50 rounded p-4 mb-4">
                  <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-300 rounded w-1/2"></div>
                </div>
                <div className="bg-white/50 rounded p-4">
                  <div className="h-4 bg-gray-300 rounded w-full mb-2"></div>
                  <div className="h-4 bg-gray-300 rounded w-5/6"></div>
                </div>
              </div>

              {/* Widget Preview */}
              <div className={getPositionStyles()}>
                {/* Chat Bubble */}
                <button
                  style={{ backgroundColor: primaryColor }}
                  className="w-14 h-14 rounded-full shadow-lg flex items-center justify-center text-white text-2xl hover:scale-110 transition"
                >
                  💬
                </button>

                {/* Chat Window (always shown in preview) */}
                <div
                  style={{
                    backgroundColor: backgroundColor,
                    color: textColor,
                  }}
                  className="absolute bottom-16 right-0 w-80 rounded-lg shadow-xl overflow-hidden"
                >
                  {/* Header */}
                  <div
                    style={{ backgroundColor: primaryColor }}
                    className="p-4 text-white"
                  >
                    <h4 className="font-semibold">AI Shopping Assistant</h4>
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <div className="bg-gray-100 rounded-lg p-3 mb-4 text-sm">
                      {greeting}
                    </div>

                    <input
                      type="text"
                      placeholder={placeholder}
                      readOnly
                      className="w-full px-4 py-2 border-2 rounded-lg text-sm"
                      style={{ borderColor: primaryColor }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <p className="text-sm text-gray-600 mt-4">
              This is how your widget will appear on your website. The actual widget will open/close on click.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
